/**
 * Reset Password Page
 * Handles password reset with token
 */

import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import AuthLayout from '../../components/auth/AuthLayout';
import FormInput from '../../components/auth/FormInput';
import Button from '../../components/auth/Button';
import Alert from '../../components/auth/Alert';
import PasswordStrengthIndicator from '../../components/auth/PasswordStrengthIndicator';
import { validateResetPasswordForm, hasErrors } from '../../utils/validation';
import { resetPassword } from '../../services/authService';
import { FormErrors, ApiError } from '../../types/auth';

const ResetPasswordPage: React.FC = () => {
  const { t } = useTranslation(['auth', 'common']);
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');

  useEffect(() => {
    if (isAuthenticated) {
      navigate('/', { replace: true });
    }
  }, [isAuthenticated, navigate]);

  // Form state
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [formErrors, setFormErrors] = useState<FormErrors>({});
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  // Check for token on mount
  useEffect(() => {
    if (!token) {
      setError(t('auth:resetPasswordPage.invalidToken'));
    }
  }, [token, t]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!token) {
      setError(t('auth:resetPasswordPage.invalidToken'));
      return;
    }

    setFormErrors({});
    setError(null);

    // Validate form
    const errors = validateResetPasswordForm({ password, confirmPassword });
    if (hasErrors(errors)) {
      setFormErrors(errors);
      return;
    }

    setIsLoading(true);

    try {
      await resetPassword(token, password);
      setSuccess(true);
      // Redirect to login after 3 seconds
      setTimeout(() => {
        navigate('/login', {
          state: { message: t('auth:resetPasswordPage.success') },
        });
      }, 3000);
    } catch (err) {
      const apiError = err as ApiError;
      if (apiError.statusCode === 400 || apiError.statusCode === 401) {
        setError(t('auth:resetPasswordPage.invalidToken'));
      } else {
        setError(apiError.message || t('auth:resetPasswordPage.resetFailed'));
      }
    } finally {
      setIsLoading(false);
    }
  };

  // Show success message
  if (success) {
    return (
      <AuthLayout
        title={t('auth:resetPasswordPage.success')}
        subtitle={t('auth:resetPasswordPage.successDescription')}
      >
        <div className="text-center">
          <div className="mb-6">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg
                className="w-8 h-8 text-green-600"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M5 13l4 4L19 7"
                />
              </svg>
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              {t('common:success')}
            </h3>
            <p className="text-gray-600 mb-4">
              {t('auth:resetPasswordPage.successDescription')}
            </p>
          </div>

          <Link to="/login">
            <Button fullWidth>
              {t('auth:verifyEmailPage.goToLogin')}
            </Button>
          </Link>
        </div>
      </AuthLayout>
    );
  }

  // Show error if no token
  if (!token) {
    return (
      <AuthLayout
        title={t('auth:resetPasswordPage.invalidToken')}
        subtitle={t('auth:resetPasswordPage.requestNew')}
      >
        <div className="text-center">
          <div className="mb-6">
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg
                className="w-8 h-8 text-red-600"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              {t('auth:resetPasswordPage.invalidToken')}
            </h3>
            <p className="text-gray-600 mb-4">
              {t('auth:resetPasswordPage.requestNew')}
            </p>
          </div>

          <div className="space-y-3">
            <Link to="/forgot-password">
              <Button fullWidth>
                {t('auth:forgotPassword.submit')}
              </Button>
            </Link>
            <Link to="/login">
              <Button fullWidth variant="outline">
                {t('auth:forgotPassword.backToLogin')}
              </Button>
            </Link>
          </div>
        </div>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout
      title={t('auth:resetPassword.title')}
      subtitle={t('auth:resetPassword.subtitle')}
    >
      {error && (
        <Alert type="error" onDismiss={() => setError(null)}>
          {error}
        </Alert>
      )}

      <form onSubmit={handleSubmit} noValidate>
        <div className="mb-4">
          <div className="relative">
            <FormInput
              label={t('auth:resetPassword.newPassword')}
              type={showPassword ? 'text' : 'password'}
              name="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              error={formErrors.password}
              placeholder={t('auth:placeholders.password')}
              autoComplete="new-password"
              disabled={isLoading}
            />
            <button
              type="button"
              className="absolute right-3 top-[38px] text-gray-400 hover:text-gray-600"
              onClick={() => setShowPassword(!showPassword)}
              tabIndex={-1}
            >
              {showPassword ? (
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.878 9.878L6.59 6.59m7.532 7.532l3.29 3.29M3 3l18 18" />
                </svg>
              ) : (
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                </svg>
              )}
            </button>
          </div>
          <PasswordStrengthIndicator password={password} />
        </div>

        <div className="relative">
          <FormInput
            label={t('auth:resetPassword.confirmPassword')}
            type={showConfirmPassword ? 'text' : 'password'}
            name="confirmPassword"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            error={formErrors.confirmPassword}
            placeholder={t('auth:placeholders.confirmPassword')}
            autoComplete="new-password"
            disabled={isLoading}
          />
          <button
            type="button"
            className="absolute right-3 top-[38px] text-gray-400 hover:text-gray-600"
            onClick={() => setShowConfirmPassword(!showConfirmPassword)}
            tabIndex={-1}
          >
            {showConfirmPassword ? (
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.878 9.878L6.59 6.59m7.532 7.532l3.29 3.29M3 3l18 18" />
              </svg>
            ) : (
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
              </svg>
            )}
          </button>
        </div>

        <Button
          type="submit"
          fullWidth
          isLoading={isLoading}
          disabled={isLoading}
        >
          {t('auth:resetPassword.submit')}
        </Button>
      </form>

      <div className="mt-6 text-center">
        <Link
          to="/login"
          className="text-sm text-gray-600 hover:text-gray-900"
        >
          <span className="inline-flex items-center">
            <svg
              className="w-4 h-4 mr-1"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M10 19l-7-7m0 0l7-7m-7 7h18"
              />
            </svg>
            {t('auth:forgotPassword.backToLogin')}
          </span>
        </Link>
      </div>
    </AuthLayout>
  );
};

export default ResetPasswordPage;
